import React, { FC } from "react";
import Badge from "orion/lib/badge";
import Popover from "orion/lib/popover";

interface ExpandableListProps {
  /**
   * Array of strings to display
   */
  items: string[];
  /**
   * Number of items to display before truncating
   * @default 2
   */
  maxVisibleItems?: number;
  /**
   * Separator between visible items
   * @default ", "
   */
  separator?: string;
  /**
   * CSS class for the badge
   */
  badgeClassName?: string;
  /**
   * Popover placement
   * @default "bottom"
   */
  popoverPlacement?: "top" | "bottom" | "left" | "right";
  /**
   * Data test ID for testing
   */
  dataTestId?: string;
}

const ExpandableList: FC<ExpandableListProps> = ({
  items = [],
  maxVisibleItems = 2,
  separator = ", ",
  badgeClassName,
  popoverPlacement = "bottom",
  dataTestId,
}) => {
  // Return empty if no items
  if (!items || items.length === 0) {
    return null;
  }

  // If items are within limit, show all
  if (items.length <= maxVisibleItems) {
    return <span data-testid={dataTestId}>{items.join(separator)}</span>;
  }

  // Split items into visible and hidden
  const visibleItems = items.slice(0, maxVisibleItems);
  const hiddenItems = items.slice(maxVisibleItems);
  const hiddenCount = hiddenItems.length;

  // Popover body showing all hidden items
  const popoverBody = () => (
    <div style={{ maxWidth: "300px", padding: "8px" }}>
      {hiddenItems.join(separator)}
    </div>
  );

  return (
    <span data-testid={dataTestId}>
      {visibleItems.join(separator)}
      {hiddenCount > 0 && (
        <>
          &nbsp;
          {/* @ts-expect-error types not compatible */}
          <Popover
            type="uncontrolled"
            placement={popoverPlacement}
            body={popoverBody}
            size="auto"
          >
            <Badge
              alignIcon="right"
              label={`+${hiddenCount}`}
              className={badgeClassName}
            />
          </Popover>
        </>
      )}
    </span>
  );
};

export default ExpandableList;
